import { confirmDiscard } from "@/lib/unsaved";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, LayoutGrid, Search } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function ServerPicker({ guild, guilds }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const list = useMemo(() => (guilds || []).filter((g) => g.name.toLowerCase().includes(q.trim().toLowerCase())), [guilds, q]);

  return (
    <div className="flex items-center gap-1.5">
      <DropdownMenu onOpenChange={(o) => !o && setQ("")}>
        <DropdownMenuTrigger asChild>
          <button data-testid="server-picker-trigger" className="flex-1 min-w-0 flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 px-2.5 py-2 text-left hover:border-zinc-700 hover:bg-zinc-900 transition-colors">
            {guild ? <img src={guild.icon} className="w-8 h-8 rounded-lg bg-zinc-800 shrink-0" alt="" /> : <div className="w-8 h-8 rounded-lg bg-zinc-800 animate-pulse shrink-0" />}
            <div className="min-w-0 flex-1">
              <div data-testid="current-server-name" className="font-head font-semibold text-sm truncate">{guild?.name || "Loading…"}</div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500">{guild?.plan || "…"} plan</div>
            </div>
            <ChevronsUpDown className="w-4 h-4 text-zinc-500 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-h-[70vh] overflow-y-auto" data-testid="server-picker-menu">
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-500">Your servers</DropdownMenuLabel>
          {(guilds?.length || 0) > 6 && (
            <div className="px-2 pb-2 relative" onKeyDown={(e) => e.stopPropagation()}>
              <Search className="w-3.5 h-3.5 absolute left-4 top-2.5 text-zinc-500" />
              <input data-testid="server-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search servers…"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md pl-7 pr-2 py-1.5 text-sm outline-none focus:border-volt" />
            </div>
          )}
          {list.map((g) => (
            <DropdownMenuItem key={g.id} data-testid={`server-picker-item-${g.id}`} onSelect={() => confirmDiscard() && navigate(`/app/g/${g.id}`)} className="gap-3 cursor-pointer">
              <img src={g.icon} alt="" className="w-6 h-6 rounded-md bg-zinc-800" />
              <span className="truncate flex-1">{g.name}</span>
              {g.id === guild?.id && <Check className="w-4 h-4 text-volt" />}
            </DropdownMenuItem>
          ))}
          {guilds && !list.length && <div className="px-2 py-3 text-sm text-zinc-500">No servers match.</div>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => confirmDiscard() && navigate("/app")} data-testid="server-picker-all" className="gap-3 cursor-pointer">
            <LayoutGrid className="w-4 h-4" /> All servers & invites
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Link to="/app" data-testid="guild-switcher" title="All servers" aria-label="All servers"
        className="w-9 h-9 shrink-0 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900 transition-colors">
        <LayoutGrid className="w-4 h-4" />
      </Link>
    </div>
  );
}
